import { z } from 'zod';
import { isoDateTimeSchema, pageQuerySchema, uuidSchema } from './common.js';
// The ceiling is declared once, in the Phase 2 storage contract, and the bucket
// configuration matches it. Redeclaring it here would be two numbers to keep
// in step, which is exactly one too many.
import { MAX_UPLOAD_BYTES } from './storage.js';
/**
 * Document intake.
 *
 * The contract for getting a file from a phone or a desktop into immutable
 * storage, with its hash, its metadata and its provenance intact. Every future
 * feature reads what this pipeline produces.
 *
 * The upload flow is three calls and one direct transfer:
 *
 *   1. POST .../intake/sessions      declare what you are sending; get a URL
 *      ↳ 409 if we already hold that hash, before a single byte moves
 *   2. PUT  <signed url>             browser → Supabase Storage, direct
 *   3. POST .../intake/sessions/:id/complete   register the document
 *
 * The bytes never pass through the API. That keeps request memory flat
 * regardless of file size and means a 25 MB scan on a slow job-site connection
 * does not occupy a Node worker for two minutes.
 */
/* -------------------------------------------------------------------------
 * File types
 * ---------------------------------------------------------------------- */
/**
 * What intake accepts.
 *
 * `parsable: false` means store-only: we keep it faithfully and nothing reads
 * it yet. Refusing a file because we cannot parse it is how a customer ends up
 * keeping a parallel paper pile, which defeats the product.
 *
 * Extensions are listed per type because browsers report MIME inconsistently —
 * Safari sends HEIC as `image/heic`, some Android builds send an empty string,
 * and Windows sends `application/octet-stream` for anything it does not know.
 * The client sniffs magic bytes and falls back to the extension.
 */
export const ACCEPTED_FILE_TYPES = [
    { mimeType: 'application/pdf', extensions: ['.pdf'], label: 'PDF', parsable: true },
    { mimeType: 'image/jpeg', extensions: ['.jpg', '.jpeg'], label: 'JPEG image', parsable: true },
    { mimeType: 'image/png', extensions: ['.png'], label: 'PNG image', parsable: true },
    { mimeType: 'image/webp', extensions: ['.webp'], label: 'WebP image', parsable: true },
    { mimeType: 'image/heic', extensions: ['.heic'], label: 'HEIC photo', parsable: true },
    { mimeType: 'image/heif', extensions: ['.heif'], label: 'HEIF photo', parsable: true },
    { mimeType: 'image/tiff', extensions: ['.tif', '.tiff'], label: 'TIFF scan', parsable: false },
    { mimeType: 'text/csv', extensions: ['.csv'], label: 'CSV', parsable: true },
    {
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extensions: ['.xlsx'],
        label: 'Excel workbook',
        parsable: false,
    },
    /* ---- v0.9, the Inbox: the door widens ---------------------------------
     *
     * Everything below is accepted at the door and identified BY ITS BYTES by
     * the server-side router — the client's claimed type is a hint, never a
     * decision. Containers unpack, bank formats queue for the import engine,
     * and a true unknown becomes an honest question in the review queue rather
     * than a rejection at the door.
     * --------------------------------------------------------------------- */
    { mimeType: 'application/zip', extensions: ['.zip'], label: 'Zip archive', parsable: false },
    {
        mimeType: 'application/x-zip-compressed',
        extensions: [],
        label: 'Zip archive (Windows)',
        parsable: false,
    },
    {
        mimeType: 'application/x-ofx',
        extensions: ['.ofx', '.qfx', '.qbo'],
        label: 'Bank export',
        parsable: false,
    },
    { mimeType: 'message/rfc822', extensions: ['.eml'], label: 'Email', parsable: false },
    { mimeType: 'text/plain', extensions: ['.txt'], label: 'Text', parsable: false },
    {
        /* The catch-all. Browsers report this for anything they do not recognise,
         * and the vision is "drop literally anything": the router sorts it out. */
        mimeType: 'application/octet-stream',
        extensions: [],
        label: 'File',
        parsable: false,
    },
];
export const ACCEPTED_MIME_TYPES = ACCEPTED_FILE_TYPES.map((type) => type.mimeType);
export const ACCEPTED_EXTENSIONS = ACCEPTED_FILE_TYPES.flatMap((type) => type.extensions);
export const uploadMimeTypeSchema = z.enum(ACCEPTED_MIME_TYPES);
/**
 * A file below this is a failed export or a truncated transfer, not a document.
 * A one-byte PDF cannot exist; the smallest valid one is a few hundred bytes.
 */
export const MIN_UPLOAD_BYTES = 64;
export const sha256Schema = z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'must be a lowercase hex SHA-256 digest');
export const DOCUMENT_SOURCES = [
    'web_desktop',
    'web_mobile',
    'mobile_camera',
    'email',
    'api',
    'bulk_import',
    'integration',
];
export const documentSourceSchema = z.enum(DOCUMENT_SOURCES);
export const INGESTION_STATES = [
    'pending_upload',
    'uploaded',
    'validated',
    'stored',
    'queued',
    'processing',
    'completed',
    'retryable',
    'failed',
    'archived',
];
export const ingestionStateSchema = z.enum(INGESTION_STATES);
/** States a user would call "still working". Drives the polling indicator. */
export const IN_FLIGHT_STATES = [
    'pending_upload',
    'uploaded',
    'validated',
    'queued',
    'processing',
];
/* -------------------------------------------------------------------------
 * Opening an upload session
 * ---------------------------------------------------------------------- */
export const createUploadSessionRequestSchema = z.object({
    filename: z
        .string()
        .trim()
        .min(1)
        .max(255)
        // Path separators and traversal are rejected outright rather than
        // sanitised, so there is no encoding that slips through. The filename is
        // metadata only — it never becomes part of the storage key.
        .refine((value) => !/[/\\]/.test(value) && !value.includes('..'), {
        message: 'filename must not contain path separators',
    }),
    byteSize: z.number().int().min(MIN_UPLOAD_BYTES).max(MAX_UPLOAD_BYTES),
    mimeType: uploadMimeTypeSchema,
    sha256: sha256Schema,
    source: documentSourceSchema.default('web_desktop'),
    /**
     * Supplied on the retry after a 409. Its presence is what turns "we already
     * have this" from a refusal into a deliberate, recorded decision.
     */
    duplicateOverrideReason: z.string().trim().min(5).max(500).optional(),
});
export const uploadSessionTicketSchema = z.object({
    sessionId: uuidSchema,
    bucket: z.string(),
    objectKey: z.string(),
    /** Signed URL the browser PUTs to. Bytes go direct; the API never sees them. */
    uploadUrl: z.url(),
    token: z.string(),
    expiresAt: isoDateTimeSchema,
});
/**
 * The body of a 409 when the hash is already held.
 *
 * Carries enough for the user to decide without another round trip: when it
 * arrived, who uploaded it, and what it was called then.
 */
export const duplicateDocumentSchema = z.object({
    documentId: uuidSchema,
    organizationId: uuidSchema,
    filename: z.string().nullable(),
    sha256: sha256Schema,
    byteSize: z.number().int().nullable(),
    uploadedAt: isoDateTimeSchema,
    uploadedByName: z.string().nullable(),
    uploadedByEmail: z.email().nullable(),
    ingestionState: ingestionStateSchema,
});
/* -------------------------------------------------------------------------
 * Completing an upload
 * ---------------------------------------------------------------------- */
export const uploadPreviewSchema = z.object({
    objectKey: z.string().min(1),
    width: z.number().int().positive().max(4096),
    height: z.number().int().positive().max(4096),
    byteSize: z
        .number()
        .int()
        .positive()
        .max(2 * 1024 * 1024),
});
export const completeUploadRequestSchema = z.object({
    bytesUploaded: z.number().int().nonnegative(),
    /**
     * Client wall clock. The server cannot measure upload duration — the bytes
     * never reach it — so the only honest source is the browser.
     */
    uploadStartedAt: isoDateTimeSchema,
    uploadFinishedAt: isoDateTimeSchema,
    /** Thumbnail generated in the browser, when one could be produced. */
    preview: uploadPreviewSchema.optional(),
    pageCount: z.number().int().positive().max(10_000).optional(),
});
export const abandonUploadRequestSchema = z.object({
    reason: z.string().trim().min(3).max(300),
});
/* -------------------------------------------------------------------------
 * Documents
 * ---------------------------------------------------------------------- */
export const documentUploaderSchema = z.object({
    userId: uuidSchema.nullable(),
    fullName: z.string().nullable(),
    email: z.email().nullable(),
});
export const documentSummarySchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    filename: z.string().nullable(),
    mimeType: z.string().nullable(),
    byteSize: z.number().int().nullable(),
    sha256: sha256Schema.nullable(),
    ingestionState: ingestionStateSchema,
    source: documentSourceSchema,
    pageCount: z.number().int().nullable(),
    receivedAt: isoDateTimeSchema,
    stateChangedAt: isoDateTimeSchema,
    failureReason: z.string().nullable(),
    duplicateOfDocumentId: uuidSchema.nullable(),
    versionCount: z.number().int().nonnegative(),
    hasPreview: z.boolean(),
    uploader: documentUploaderSchema,
});
export const documentVersionSchema = z.object({
    id: uuidSchema,
    versionNumber: z.number().int().positive(),
    filename: z.string(),
    mimeType: z.string(),
    byteSize: z.number().int().positive(),
    sha256: sha256Schema,
    pageCount: z.number().int().nullable(),
    reason: z.string().nullable(),
    isCurrent: z.boolean(),
    hasPreview: z.boolean(),
    checksumVerifiedAt: isoDateTimeSchema.nullable(),
    checksumVerificationFailedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    createdBy: documentUploaderSchema,
});
export const documentStateEventSchema = z.object({
    id: uuidSchema,
    fromState: ingestionStateSchema.nullable(),
    toState: ingestionStateSchema,
    actorType: z.enum(['user', 'system', 'service']),
    reason: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    occurredAt: isoDateTimeSchema,
});
export const documentDetailSchema = documentSummarySchema.extend({
    versions: z.array(documentVersionSchema),
    stateEvents: z.array(documentStateEventSchema),
    notes: z.string().nullable(),
    duplicateOverrideReason: z.string().nullable(),
});
/* -------------------------------------------------------------------------
 * Search
 * ---------------------------------------------------------------------- */
export const DOCUMENT_SORT_FIELDS = ['received_at', 'filename', 'byte_size'];
export const listDocumentsQuerySchema = pageQuerySchema.extend({
    /** Substring match on filename. Backed by a trigram index. */
    query: z.string().trim().min(1).max(200).optional(),
    state: ingestionStateSchema.optional(),
    source: documentSourceSchema.optional(),
    uploaderId: uuidSchema.optional(),
    /** Exact hash lookup — the "is this already here" question. */
    sha256: sha256Schema.optional(),
    mimeType: z.string().min(1).max(120).optional(),
    receivedFrom: isoDateTimeSchema.optional(),
    receivedTo: isoDateTimeSchema.optional(),
    includeArchived: z.coerce.boolean().default(false),
    sort: z.enum(DOCUMENT_SORT_FIELDS).default('received_at'),
    direction: z.enum(['asc', 'desc']).default('desc'),
});
export const documentDownloadRequestSchema = z.object({
    versionId: uuidSchema.optional(),
    expiresInSeconds: z.number().int().min(30).max(3600).default(300),
    /** A preview link rather than the original. */
    preview: z.boolean().default(false),
});
export const documentDownloadTicketSchema = z.object({
    documentId: uuidSchema,
    versionId: uuidSchema,
    filename: z.string(),
    mimeType: z.string(),
    byteSize: z.number().int(),
    downloadUrl: z.url(),
    expiresAt: isoDateTimeSchema,
});
/* -------------------------------------------------------------------------
 * Observability
 * ---------------------------------------------------------------------- */
export const intakeStatsSchema = z.object({
    windowDays: z.number().int().positive(),
    totals: z.object({
        documents: z.number().int().nonnegative(),
        storageBytes: z.number().int().nonnegative(),
        uploadsAttempted: z.number().int().nonnegative(),
        uploadsCompleted: z.number().int().nonnegative(),
        uploadsFailed: z.number().int().nonnegative(),
        duplicatesBlocked: z.number().int().nonnegative(),
        duplicatesOverridden: z.number().int().nonnegative(),
    }),
    rates: z.object({
        /** completed ÷ attempted, 0..1. */
        uploadSuccess: z.number().min(0).max(1).nullable(),
        /** (blocked + overridden) ÷ attempted, 0..1. */
        duplicate: z.number().min(0).max(1).nullable(),
    }),
    timing: z.object({
        averageUploadMs: z.number().int().nonnegative().nullable(),
        p95UploadMs: z.number().int().nonnegative().nullable(),
        averageBytes: z.number().int().nonnegative().nullable(),
        largestBytes: z.number().int().nonnegative().nullable(),
    }),
    byState: z.array(z.object({ state: ingestionStateSchema, documentCount: z.number().int().nonnegative() })),
    bySource: z.array(z.object({ source: documentSourceSchema, documentCount: z.number().int().nonnegative() })),
    queue: z.array(z.object({
        jobType: z.string(),
        status: z.enum(['pending', 'running', 'succeeded', 'failed', 'dead', 'cancelled']),
        jobCount: z.number().int().nonnegative(),
        totalAttempts: z.number().int().nonnegative(),
        oldestPendingAt: isoDateTimeSchema.nullable(),
    })),
});
/**
 * Whether a file may be uploaded at all.
 *
 * Lives in the contract package so the browser rejects it before hashing and
 * the API rejects it again on arrival, using identical rules. Two
 * implementations of the same rule is how a client-side check quietly diverges
 * into a lie.
 */
export function validateUploadCandidate(input) {
    if (input.filename.length > 255) {
        return { reason: 'name_too_long', message: 'The file name is longer than 255 characters.' };
    }
    if (/[/\\]/.test(input.filename) || input.filename.includes('..')) {
        return { reason: 'name_unsafe', message: 'The file name contains path characters.' };
    }
    if (input.byteSize === 0) {
        return { reason: 'empty', message: 'This file is empty.' };
    }
    if (input.byteSize < MIN_UPLOAD_BYTES) {
        return {
            reason: 'too_small',
            message: `This file is only ${input.byteSize} bytes, which is too small to be a real document.`,
        };
    }
    if (input.byteSize > MAX_UPLOAD_BYTES) {
        return {
            reason: 'too_large',
            message: `This file is ${formatBytes(input.byteSize)}. The limit is ${formatBytes(MAX_UPLOAD_BYTES)}.`,
        };
    }
    if (!ACCEPTED_MIME_TYPES.includes(input.mimeType)) {
        return {
            reason: 'unsupported_type',
            message: `${describeType(input.filename)} files are not supported.`,
        };
    }
    return null;
}
/** Resolves a MIME type from a filename, for browsers that report nothing useful. */
export function mimeTypeFromFilename(filename) {
    const dot = filename.lastIndexOf('.');
    if (dot < 0)
        return null;
    const extension = filename.slice(dot).toLowerCase();
    const match = ACCEPTED_FILE_TYPES.find((type) => type.extensions.includes(extension));
    return match?.mimeType ?? null;
}
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex] ?? 'GB'}`;
}
function describeType(filename) {
    const dot = filename.lastIndexOf('.');
    return dot < 0 ? 'These' : filename.slice(dot + 1).toUpperCase();
}
/**
 * The Inbox triage line: what arrived lately, and what of it needs a person.
 *
 * "25 new: 12 receipts, 4 invoices, 2 statements, 2 duplicates, 1 unknown" is
 * the sentence this exists to produce. Counts, not judgements — every number
 * is a query a person could rerun.
 */
export const inboxSummarySchema = z.object({
    /** Documents that arrived in the window, by what the router concluded. */
    arrivedTotal: z.number().int(),
    byKind: z.record(z.string(), z.number().int()),
    /** Work waiting on a person, wherever it lives. */
    needsYou: z.object({
        review: z.number().int(),
        statements: z.number().int(),
        proposals: z.number().int(),
        unknown: z.number().int(),
    }),
    windowDays: z.number().int(),
});
//# sourceMappingURL=intake.js.map